import React, { useRef, useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Film, FolderKanban, Play } from 'lucide-react';
import { DemoStage } from './DemoChrome';
import { gentleCrossfade } from './motionPresets';
import { useDemoAutoplay } from './useDemoAutoplay';
import {
  DEMO_LIBRARY_VIDEO_THUMB,
  DEMO_PIPELINE_MEDIA_THUMB,
  DEMO_PIPELINE_DRAFT_THUMB,
  DEMO_REVIEW_FRAME_STILLS,
} from './demoStockImages';
import './marketingDemos.css';

const WORKSPACE_ORDER = ['acme', 'north', 'studio'];

const WORKSPACES = {
  acme: {
    name: 'Acme Co.',
    color: '#3b82f6',
    members: 3,
    summary: 'March campaign · Instagram + YouTube',
    assets: [
      {
        kind: 'project',
        title: 'March campaign hub',
        status: 'Project',
        projectColor: '#6366f1',
      },
      {
        kind: 'media',
        title: 'Brand interview',
        thumb: DEMO_LIBRARY_VIDEO_THUMB,
        duration: '2:14',
        status: 'Approved',
      },
      {
        kind: 'media',
        title: 'March reel b-roll',
        thumb: DEMO_PIPELINE_MEDIA_THUMB,
        duration: '0:42',
        status: 'In review',
      },
    ],
  },
  north: {
    name: 'Northwind',
    color: '#0f766e',
    members: 5,
    summary: 'Q2 planning · isolated library',
    assets: [
      {
        kind: 'media',
        title: 'Founder story',
        thumb: DEMO_REVIEW_FRAME_STILLS[0],
        duration: '4:06',
        status: 'Library',
      },
      {
        kind: 'project',
        title: 'Q2 product launch',
        status: 'Project',
        projectColor: '#0f766e',
      },
      {
        kind: 'media',
        title: 'Outdoor product take',
        thumb: DEMO_REVIEW_FRAME_STILLS[1],
        duration: '1:18',
        status: 'Changes requested',
      },
    ],
  },
  studio: {
    name: 'Studio Nine',
    color: '#7c3aed',
    members: 4,
    summary: 'Client portal on · short-form only',
    assets: [
      {
        kind: 'media',
        title: 'Short hook clip',
        thumb: DEMO_REVIEW_FRAME_STILLS[2],
        duration: '0:22',
        status: 'Scheduled',
      },
      {
        kind: 'media',
        title: 'Client review cut',
        thumb: DEMO_PIPELINE_DRAFT_THUMB,
        duration: '1:05',
        status: 'Client review',
      },
      {
        kind: 'project',
        title: 'Client portal kit',
        status: 'Project',
        projectColor: '#7c3aed',
      },
    ],
  },
};

function WorkspaceAssetCard({ asset }) {
  if (asset.kind === 'project') {
    return (
      <div className="mkt-workspace-asset-card mkt-workspace-asset-card--project">
        <div
          className="mkt-workspace-asset-thumb mkt-workspace-asset-thumb--project"
          style={{ '--workspace-project-accent': asset.projectColor }}
        >
          <FolderKanban className="mkt-workspace-asset-project-icon" aria-hidden />
        </div>
        <div className="mkt-workspace-asset-body">
          <p className="mkt-workspace-asset-title">{asset.title}</p>
          <span className="mkt-workspace-asset-status mkt-workspace-asset-status--project">
            {asset.status}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="mkt-workspace-asset-card">
      <div className="mkt-workspace-asset-thumb">
        <img src={asset.thumb} alt="" loading="eager" decoding="async" />
        <Play
          size="1rem"
          className="mkt-workspace-asset-play"
          fill="rgba(255,255,255,0.35)"
          color="rgba(255,255,255,0.92)"
          aria-hidden
        />
        <span className="mkt-workspace-asset-duration">{asset.duration}</span>
      </div>
      <div className="mkt-workspace-asset-body">
        <p className="mkt-workspace-asset-title">{asset.title}</p>
        <span
          className={`mkt-workspace-asset-status mkt-workspace-asset-status--${asset.status
            .toLowerCase()
            .replace(/\s+/g, '-')}`}
        >
          {asset.status}
        </span>
      </div>
    </div>
  );
}

export default function WorkspaceSwitcherDemo() {
  const rootRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const onAutoplayTick = useCallback(() => {
    setDropdownOpen((open) => {
      if (!open) return true;
      setActiveIndex((i) => (i + 1) % WORKSPACE_ORDER.length);
      return false;
    });
  }, []);

  useDemoAutoplay(rootRef, onAutoplayTick, 1400);

  useEffect(() => {
    const preloadSources = new Set();
    Object.values(WORKSPACES).forEach((ws) => {
      ws.assets.forEach((asset) => {
        if (asset.kind === 'media' && asset.thumb) {
          preloadSources.add(asset.thumb);
        }
      });
    });
    preloadSources.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  const activeId = WORKSPACE_ORDER[activeIndex];
  const active = WORKSPACES[activeId];

  return (
    <div ref={rootRef}>
      <DemoStage visualClassName="mkt-demo-visual--workspaces">
        <div className="mkt-workspace-shell">
          <aside className="mkt-workspace-sidebar">
            <div className="mkt-workspace-dropdown">
              <p className="mkt-workspace-dropdown-label">Active workspace</p>
              <div className="mkt-workspace-dropdown-anchor">
                <div
                  className={`mkt-workspace-dropdown-trigger mkt-workspace-dropdown-trigger--auto${
                    dropdownOpen ? ' mkt-workspace-dropdown-trigger--open' : ''
                  }`}
                >
                  <span
                    className="mkt-workspace-dropdown-trigger-dot"
                    style={{ background: active.color }}
                    aria-hidden
                  />
                  <span className="mkt-workspace-dropdown-trigger-copy">
                    <span className="mkt-workspace-dropdown-trigger-name">{active.name}</span>
                    <span className="mkt-workspace-dropdown-trigger-meta">
                      {active.members} members
                      <span className="mkt-workspace-dropdown-trigger-summary">
                        {' · '}
                        {active.summary}
                      </span>
                    </span>
                  </span>
                  <ChevronDown
                    className={`ui-icon-lg mkt-workspace-dropdown-chevron${
                      dropdownOpen ? ' mkt-workspace-dropdown-chevron--open' : ''
                    }`}
                    aria-hidden
                  />
                </div>

                <AnimatePresence>
                  {dropdownOpen ? (
                    <motion.div
                      className="mkt-workspace-dropdown-menu"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.18 }}
                    >
                      {WORKSPACE_ORDER.map((id, index) => {
                        const ws = WORKSPACES[id];
                        const selected = index === activeIndex;
                        return (
                          <div
                            key={id}
                            className={`mkt-workspace-dropdown-option mkt-workspace-dropdown-option--auto${
                              selected ? ' mkt-workspace-dropdown-option--selected' : ''
                            }`}
                          >
                            <span
                              className="mkt-workspace-dropdown-option-dot"
                              style={{ background: ws.color }}
                              aria-hidden
                            />
                            <span className="mkt-workspace-dropdown-option-copy">
                              <span className="mkt-workspace-dropdown-option-name">{ws.name}</span>
                              <span className="mkt-workspace-dropdown-option-meta">
                                {ws.members} members
                              </span>
                            </span>
                            {selected ? <Check className="ui-icon-md" aria-hidden /> : null}
                          </div>
                        );
                      })}
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </div>
            </div>
          </aside>

          <div className="mkt-demo-crossfade-stage mkt-workspace-library-stage">
            <AnimatePresence mode="sync" initial={false}>
              <motion.div
                key={activeId}
                className="mkt-workspace-library"
                variants={gentleCrossfade}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <div className="mkt-workspace-library-header">
                  <Film className="ui-icon-md" aria-hidden />
                  <span>Library</span>
                  <span className="mkt-workspace-library-count">{active.assets.length} items</span>
                </div>

                <div className="mkt-workspace-asset-grid">
                  {active.assets.map((asset) => (
                    <WorkspaceAssetCard key={asset.title} asset={asset} />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </DemoStage>
    </div>
  );
}
